import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Creatable from 'react-select/creatable';
import { selectCustomStyles } from '../../common/shared/select.styled';
import { constants } from '../../common/constants/constants';
import { useAccessToken } from '../../common/hooks/useAccessToken';
import { showLoader, hideLoader } from '../../store/common/slice';
import { StyledButton } from "./NewBuildingAddressPopUp.styled";
import CacheData from "../../common/utils/cacheData";
import {NotificationManager} from "react-notifications";

const NewAddress = ({ address, entranceNumber, photos, addressData, modal, buildingID }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const client = useAccessToken();

    const [cities, setCities] = useState([]);
    const [inputs, setInputs] = useState({
        street_name: '',
        street_number: '',
        entrance_number: '',
        nickname: '',
    });

    const [citiesDropdown, setCitiesDropdown] = useState([]);
    const [countriesDropdown, setCountriesDropdown] = useState([
        {
            value: 'ישראל',
            label: 'ישראל'
        }
    ]);
    const [title, setTitle] = useState('');
    const [currentCity, setCurrentCity] = useState({
        value: '',
        label: ''
    });
    const [currentCountry, setCurrentCountry] = useState({
        value: '',
        label: ''
    });
    const [updateBtnDisabled, setUpdateBtnDisabled] = useState(true);

    useEffect(async () => {
        if (cities.length === 0) {
            // load dynamically to exclude from common bundle
            const module = await import('../../common/simple/citiesInIsrael.json');
            setCities(module.default);
        }
    });

    useEffect(() => {
        const mappedCities = cities.map(city => ({ value: city.cityName, label: city.cityName, }));
        setCitiesDropdown(mappedCities);
    }, [cities]);

    useEffect(() => {
        setInputs({
            ...inputs,
            entrance_number: entranceNumber
        })
    }, [entranceNumber])

    useEffect(() => {
        if (address) {
            let inputValues = {};
            for (const item of address) {
                const type = item?.types;
                const name = item?.long_name;
                if (type?.includes('route')) inputValues.street_name = name;
                else if (type?.includes('locality')) {
                    setCurrentCity({
                        value: name,
                        label: name
                    })
                } else if (type?.includes('street_number')) inputValues.street_number = name;
                else if (type?.includes('country')) {
                    setCurrentCountry({
                        value: name,
                        label: name
                    })
                } else if (type?.includes('nickname')) {
                    inputValues.nickname = name;
                }
            }
            setInputs(inputValues);
        }
    }, [address])

    const onInputChange = e => {
        const targ = e.target;
        setInputs({
            ...inputs,
            [targ.name]: targ.value
        })
    };

    const onCityDropdownChange = e => setCurrentCity(e);

    const onCountryDropdownChange = e => setCurrentCountry(e);

    const onCreatingNewCityDropdownOption = value => {
        setCitiesDropdown([
            ...citiesDropdown,
            {
                value: value,
                label: value
            }
        ]);
        setCurrentCity({
            value: value,
            label: value
        });
    };

    const onCreatingNewCountryDropdownOption = value => {
        setCountriesDropdown([
            ...citiesDropdown,
            {
                value: value,
                label: value
            }
        ]);
        setCurrentCountry({
            value: value,
            label: value
        });
    };

    const onTitleChange = e => setTitle(e.target.value);

    useEffect(() => {
        const { street_name, street_number, entrance_number } = inputs;
        setTitle(`${street_name} ${street_number}, ${currentCity.label} ${entrance_number ? `${t('buildings.entrance')} ${entrance_number}` : ''}`)
    }, [currentCountry, currentCity, inputs]);

    const checkIfDisabled = () => {
        if (inputs.street_name && inputs.street_number && currentCity.label && currentCountry.label) setUpdateBtnDisabled(false)
        else setUpdateBtnDisabled(true)
    };

    useEffect(() => {
        checkIfDisabled()
    }, [inputs, currentCountry, currentCity])

    const createBuildingPayload = (isUpdatingBuilding) => {
        if (isUpdatingBuilding){
            return createPayloadForUpdateBuilding();
        }else{
            return createPayloadForCreateBuilding();
        }

    };


    const createPayloadForCreateBuilding = () => {
        const addressComponents = [
            {"long_name": inputs.street_number,"short_name": inputs.street_number,"types": ["street_number"]},
            {"long_name": inputs.street_name,"short_name": inputs.street_name,"types": ["route"]},
            {"long_name": currentCity.label,"short_name": currentCity.label,"types": ["locality","political"]},
            {"long_name": currentCountry.label,"short_name": currentCountry.label,"types": ["country","political"]}
        ];

        return {
            "address_components": addressComponents,
            "address_data": addressData,
            "entrance": inputs.entrance,
            "hosts": photos,
            "AgreeToAds": true
        }
    };

    const createPayloadForUpdateBuilding = () => {
        let data = {
            "title": title,
            "city": currentCity.label,
            "street": inputs.street_name,
            "streetNumber": inputs.street_number,
            "country": currentCountry.label,
            "nickname": inputs.nickname,
        }

        if (inputs.entrance){
            data.entrance = inputs.entrance;
        }

        return {
            address: data
        }
    };

    const history = useHistory();

    const onUpdate = async () => {
        const isUpdateBuilding = buildingID;
        let data = createBuildingPayload(isUpdateBuilding);
        if (data) {

            dispatch(showLoader());
            let response;
            if (isUpdateBuilding){
                response = await client.post(`/managers/buildings/${buildingID}`, data);
                if (response && response.updated > 0) {
                    // clear cache
                    NotificationManager.success(t('statuses.success_action_notification'), '', 5000);
                    CacheData.delete('buildingData', {buildingID: buildingID});
                    CacheData.delete('managerData', {buildingID: buildingID});
                    window.location.reload();
                }

            }else{
                response = await client.put('/managers/buildings', data);
            }

            dispatch(hideLoader());

            if (response.buildingID) {
                history.push({
                    pathname: `/${constants.pages.addBankDetailsUrl}/${response.buildingID}`
                });
            }
        }
    };

    return (
        <>
            <div className="inputs-wrapper">
                <div>
                    <label htmlFor="street_name">{t('buildings.address_pop_up.labels.street_name')}</label>
                    <input
                        name="street_name"
                        id="street_name"
                        className="form-section small-input"
                        value={inputs.street_name}
                        onChange={onInputChange}
                    />
                </div>

                <div>
                    <label htmlFor="street_number">{t('buildings.address_pop_up.labels.street_number')}</label>
                    <input
                        name="street_number"
                        id="street_number"
                        className="form-section small-input"
                        value={inputs.street_number}
                        onChange={onInputChange}
                    />
                </div>

                <div>
                    <label>{t('buildings.address_pop_up.labels.city')}</label>
                    <Creatable
                        styles={selectCustomStyles}
                        className='dropdown'
                        options={citiesDropdown}
                        value={currentCity}
                        onChange={onCityDropdownChange}
                        onCreateOption={onCreatingNewCityDropdownOption}
                    />
                </div>

                <div>
                    <label>{t('buildings.address_pop_up.labels.country')}</label>
                    <Creatable
                        styles={selectCustomStyles}
                        className='dropdown'
                        options={countriesDropdown}
                        value={currentCountry}
                        onChange={onCountryDropdownChange}
                        onCreateOption={onCreatingNewCountryDropdownOption}
                    />
                </div>

                <div>
                    <label htmlFor="entrance_number">{t('buildings.address_pop_up.labels.entrance')}</label>
                    <input
                        name="entrance_number"
                        id="entrance_number"
                        className="form-section small-input"
                        value={inputs.entrance_number}
                        onChange={onInputChange}
                    />
                </div>
            </div>

            <div className="title-wrapper">
                <label htmlFor="building_title">{t('buildings.address_pop_up.labels.building_title')}</label>
                <input
                    name="building_title"
                    id="building_title"
                    className="form-section small-input title-input"
                    value={title}
                    onChange={onTitleChange}
                />
            </div>

            <div className="nickname-wrapper">
                <label htmlFor="nickname">Nickname (in case address does not appear in google/special name for building)</label>
                <input
                    name="nickname"
                    id="nickname"
                    className="form-section small-input"
                    value={inputs.nickname}
                    onChange={onInputChange}
                />
            </div>

            <StyledButton onClick={onUpdate} disabled={updateBtnDisabled}>{t('buildings.address_pop_up.update')}</StyledButton>
        </>
    );
};

export default NewAddress;
